import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Undo2, Pencil, Ban } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inr, num, fmtDate, fmtDateTime } from "../lib/format";
import { Loading, Empty } from "../components/kit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const Sel = ({ value, onChange, items, placeholder, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="h-12 text-base" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

function ReturnForm({ editing, onClose, onDone, cats, reasons }) {
  const { user, settings, storeId } = useAuth();
  const isStaff = user.role === "staff";
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState(editing?.staff_id || (isStaff ? user.id : ""));
  const [sources, setSources] = useState([]);
  const blank = { entry_id: "none", date: settings.effective_date, bill_number: "", product_category: "", product_type: "",
    amount: "", qty: "", full_bill: false, reason: "", remarks: "", correction_reason: "" };
  const [f, setF] = useState(editing ? { ...blank, ...editing, entry_id: editing.entry_id || "none", correction_reason: "" } : blank);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isStaff) api.get("/users", { params: { role: "staff", store_id: storeId !== "all" ? storeId : undefined } }).then((r) => setStaff(r.data));
  }, [isStaff, storeId]);
  useEffect(() => {
    if (staffId) api.get("/returns/sources", { params: { staff_id: isStaff ? undefined : staffId } }).then((r) => setSources(r.data));
  }, [staffId, isStaff]);

  const src = sources.find((s) => s.id === f.entry_id);
  const k = f.product_category;
  const own = (field) => (editing && editing.entry_id === f.entry_id && editing.product_category === k ? editing[field] || 0 : 0);
  const left = src && k ? (src.categories[k] || 0) - (src.returned[k] || 0) + own("amount") : null;
  const leftQ = src && k ? (src.qty?.[k] || 0) - (src.returned_qty[k] || 0) + own("qty") : null;

  const save = async () => {
    setErr("");
    if (!isStaff && !staffId) return setErr("Select the sales staff (whose customer).");
    if (!k) return setErr("Select the product category.");
    if (!(Number(f.amount) > 0)) return setErr("Enter the return amount.");
    if (!(Number(f.qty) >= 1)) return setErr("Enter the number of products returned.");
    const body = { ...f, entry_id: f.entry_id === "none" ? null : f.entry_id, staff_id: isStaff ? undefined : staffId,
      amount: Number(f.amount), qty: Math.round(Number(f.qty)) };
    try {
      if (editing) await api.put(`/returns/${editing.id}`, body);
      else await api.post("/returns", body);
      toast.success(editing ? "Return corrected" : "Return recorded");
      onDone();
    } catch (e) { setErr(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">{editing ? "Correct return" : "New return"}</DialogTitle></DialogHeader>
        <div className="grid gap-4" data-testid="return-form">
          {!isStaff && !editing && (
            <div className="space-y-1.5"><Label>Sales staff (whose customer) *</Label>
              <Sel value={staffId} onChange={(v) => { setStaffId(v); setF({ ...f, entry_id: "none" }); }} items={staff.map((s) => [s.id, `${s.name} · ${s.employee_id}`])} placeholder="Select staff" testid="return-staff-select" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Product category *</Label><Sel value={f.product_category} onChange={(v) => setF({ ...f, product_category: v })} items={cats.map((c) => [c.key, c.name])} placeholder="Category" testid="return-category-select" /></div>
            <div className="space-y-1.5"><Label>Return date *</Label><Input type="date" className="h-12" min={src?.date} max={settings.effective_date} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="return-date-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Return amount (₹) *</Label><Input type="number" inputMode="decimal" min="0" className="h-14 text-xl num font-semibold" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="return-amount-input" /></div>
            <div className="space-y-1.5"><Label>Products returned *</Label><Input type="number" inputMode="numeric" min="1" className="h-14 text-xl num font-semibold" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="return-qty-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Original sale (optional)</Label>
            <Sel value={f.entry_id} onChange={(v) => setF({ ...f, entry_id: v })} testid="return-source-select"
              items={[["none", "Not linked to a sale day"], ...sources.map((s) => [s.id, `${fmtDate(s.date)} · ${inr(s.sales)} · ${s.bills} bills`])]} />
            {left != null && <p className="text-xs text-slate-500" data-testid="return-left-hint">Left to return on that day: {inr(left)} · {leftQ} products</p>}
            {editing && <p className="text-xs text-slate-500">The original sale cannot be changed. Void and re-create if it is wrong.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Bill number (optional)</Label><Input className="h-12" value={f.bill_number} onChange={(e) => setF({ ...f, bill_number: e.target.value })} data-testid="return-bill-input" /></div>
            <div className="space-y-1.5"><Label>Product type (optional)</Label><Input className="h-12" placeholder="Ring, earring…" value={f.product_type} onChange={(e) => setF({ ...f, product_type: e.target.value })} data-testid="return-type-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.full_bill} onCheckedChange={(c) => setF({ ...f, full_bill: !!c })} data-testid="return-full-bill-check" /> Entire bill returned (reduces net bills)</label>
          <div className="space-y-1.5"><Label>Return reason (optional)</Label><Sel value={f.reason} onChange={(v) => setF({ ...f, reason: v })} items={reasons.map((r) => [r, r])} placeholder="Select reason" testid="return-reason-select" /></div>
          <div className="space-y-1.5"><Label>Remarks (optional)</Label><Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="return-remarks-input" /></div>
          {editing && <div className="space-y-1.5"><Label>Correction reason</Label><Input className="h-12" value={f.correction_reason} onChange={(e) => setF({ ...f, correction_reason: e.target.value })} data-testid="return-correction-reason-input" /></div>}
          {err && <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="return-error">{err}</div>}
        </div>
        <DialogFooter><Button onClick={save} className="h-12 w-full bg-[#011E38]" data-testid="return-save-button">{editing ? "Save correction" : "Record return"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoidDialog({ ret, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const run = async () => {
    try { await api.post(`/returns/${ret.id}/void`, { reason }); toast.success("Return voided"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Void return · {ret.bill_number || inr(ret.amount)}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">The {inr(ret.amount)} will be added back to net sales. The record stays in the audit trail.</p>
        <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="void-reason-input" />
        <DialogFooter><Button disabled={!reason.trim()} onClick={run} className="bg-red-700 hover:bg-red-800" data-testid="void-confirm-button">Void return</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Returns() {
  const { user, month, settings, storeId } = useAuth();
  const isStaff = user.role === "staff";
  const m = isStaff ? settings.effective_date.slice(0, 7) : month;
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState(null);
  const [voiding, setVoiding] = useState(null);

  const load = useCallback(() => {
    const params = { month: m, store_id: !isStaff && storeId !== "all" ? storeId : undefined };
    Promise.all([api.get("/returns", { params }), api.get("/categories"), api.get("/returns/meta")])
      .then(([r, c, mt]) => { setRows(r.data); setCats(c.data); setReasons(mt.data.reasons); });
  }, [m, isStaff, storeId]);
  useEffect(() => { load(); }, [load]);
  const done = () => { setForm(null); setVoiding(null); load(); };
  const active = (rows || []).filter((r) => r.status === "active");
  const cname = (k) => cats.find((c) => c.key === k)?.name || k;

  return (
    <div className="space-y-6 bs-rise">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="bs-eyebrow">Returns · {m}</div>
          <h1 className="font-display text-4xl font-extrabold text-[#011E38] tracking-tight mt-1" data-testid="returns-title">Returns</h1>
        </div>
        <Button onClick={() => setForm({})} className="h-12 rounded-full px-5 bg-[#011E38]" data-testid="new-return-button"><Plus className="w-4 h-4 mr-1.5" /> New return</Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bs-card p-4"><div className="bs-eyebrow">Returns</div><div className="num text-2xl sm:text-3xl font-bold text-red-700 mt-1" data-testid="returns-total-amount">-{inr(active.reduce((a, r) => a + r.amount, 0))}</div></div>
        <div className="bs-card p-4"><div className="bs-eyebrow">Products</div><div className="num text-2xl sm:text-3xl font-bold text-[#011E38] mt-1" data-testid="returns-total-qty">{num(active.reduce((a, r) => a + r.qty, 0))}</div></div>
        <div className="bs-card p-4"><div className="bs-eyebrow">Count</div><div className="num text-2xl sm:text-3xl font-bold text-[#011E38] mt-1" data-testid="returns-count">{active.length}</div></div>
      </div>
      <p className="text-xs text-slate-500">Returns never change the original sale. They reduce net sales and net products for the return month.</p>
      {!rows ? <Loading /> : !rows.length ? <Empty>No returns this month.</Empty> : (
        <div className="space-y-3" data-testid="returns-list">
          {rows.map((r) => (
            <div key={r.id} className={`bs-card p-4 ${r.status === "void" ? "opacity-60" : ""}`} data-testid={`return-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#011E38]"><Undo2 className="w-4 h-4 text-red-700" /> {cname(r.product_category)}{r.bill_number && <span className="text-slate-500 font-normal">· Bill {r.bill_number}</span>}
                    {r.status === "void" && <span className="text-xs rounded-full bg-slate-200 px-2 py-0.5" data-testid="return-void-badge">Void</span>}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtDate(r.date)}{r.original_date && ` · sale ${fmtDate(r.original_date)}`}{!isStaff && ` · ${r.staff_name}`}</div>
                  <div className="text-xs text-slate-500">{[r.product_type, r.reason, r.full_bill && "full bill"].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="text-[0.65rem] text-slate-400 mt-1">by {r.created_by_name || r.created_by} · {fmtDateTime(r.created_at)}{r.history?.length ? ` · ${r.history.length} change(s)` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="num text-xl font-bold text-red-700">-{inr(r.amount)}</div>
                  <div className="text-xs text-slate-500">{r.qty} product{r.qty !== 1 ? "s" : ""}</div>
                  {!isStaff && r.status === "active" && (
                    <div className="flex gap-1 justify-end mt-2">
                      <Button variant="ghost" size="sm" onClick={() => setForm(r)} data-testid={`correct-return-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setVoiding(r)} data-testid={`void-return-${r.id}`}><Ban className="w-4 h-4 text-red-700" /></Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {form && <ReturnForm editing={form.id ? form : null} onClose={() => setForm(null)} onDone={done} cats={cats} reasons={reasons} />}
      {voiding && <VoidDialog ret={voiding} onClose={() => setVoiding(null)} onDone={done} />}
    </div>
  );
}
